/**
 * useHealthData — HealthKit (iOS) + Health Connect (Android) integration
 *
 * Reads HRV, resting heart rate, and sleep quality passively from the device.
 * Computes a biometric_score (0–100) that can enrich the ERI display.
 * Only runs on native platforms — degrades gracefully on web.
 *
 * Scoring rationale (clinically referenced):
 *   HRV:         20ms → score 0   |  80ms → score 100  (RMSSD population norms)
 *   Resting HR:  80bpm → score 0  |  40bpm → score 100  (lower = better regulation)
 *   Sleep:       % of deep + REM out of total sleep time, mapped 0–100
 *   biometric_score: weighted average of available sub-scores
 */

import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY   = 'hyp_biometrics_cache';
const CACHE_TTL_MS = 4 * 60 * 60 * 1000; // re-read at most every 4h

// ── Scoring helpers ───────────────────────────────────────────────────────────

function hrvScore(ms) {
  if (!ms || ms <= 0) return null;
  // Linear map: 20ms → 0, 80ms → 100
  return Math.min(100, Math.max(0, Math.round(((ms - 20) / 60) * 100)));
}

function restingHRScore(bpm) {
  if (!bpm || bpm <= 0) return null;
  // Inverted linear map: 80bpm → 0, 40bpm → 100
  return Math.min(100, Math.max(0, Math.round(((80 - bpm) / 40) * 100)));
}

function sleepScore(samples) {
  if (!samples?.length) return null;
  const SLEEP_STATES = ['asleep', 'deep', 'light', 'rem'];
  const QUALITY_STATES = ['deep', 'rem'];

  const totalMin = samples
    .filter(s => SLEEP_STATES.includes(s.sleepState))
    .reduce((acc, s) => acc + (new Date(s.endDate) - new Date(s.startDate)) / 60000, 0);

  const qualityMin = samples
    .filter(s => QUALITY_STATES.includes(s.sleepState))
    .reduce((acc, s) => acc + (new Date(s.endDate) - new Date(s.startDate)) / 60000, 0);

  if (!totalMin) return null;
  return Math.min(100, Math.round((qualityMin / totalMin) * 100));
}

// ── Read from device ──────────────────────────────────────────────────────────

export async function readHealthData(userId) {
  if (!Capacitor.isNativePlatform()) return null;

  // Return cached data if still fresh
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    if (cached.userId === userId && Date.now() - cached.ts < CACHE_TTL_MS) {
      return cached.data;
    }
  } catch {}

  try {
    const { Health } = await import('@capgo/capacitor-health');

    const avail = await Health.isAvailable();
    if (!avail.available) return null;

    // Request permission — only prompts once; silent on subsequent calls
    await Health.requestAuthorization({
      read: ['heartRateVariability', 'restingHeartRate', 'sleep'],
    });

    const now = new Date();
    const sevenDaysAgo = new Date(now - 7 * 86400000);
    const opts = { startDate: sevenDaysAgo.toISOString(), endDate: now.toISOString() };

    const [hrvRes, rhrRes, sleepRes] = await Promise.allSettled([
      Health.readSamples({ dataType: 'heartRateVariability', ...opts, limit: 30 }),
      Health.readSamples({ dataType: 'restingHeartRate',     ...opts, limit: 7 }),
      Health.readSamples({ dataType: 'sleep',                ...opts, limit: 200 }),
    ]);

    const hrvSamples   = hrvRes.status   === 'fulfilled' ? hrvRes.value.samples   : [];
    const rhrSamples   = rhrRes.status   === 'fulfilled' ? rhrRes.value.samples   : [];
    const sleepSamples = sleepRes.status === 'fulfilled' ? sleepRes.value.samples : [];

    const avgHRV = hrvSamples.length
      ? hrvSamples.reduce((a, s) => a + s.value, 0) / hrvSamples.length
      : null;
    const avgRHR = rhrSamples.length
      ? rhrSamples.reduce((a, s) => a + s.value, 0) / rhrSamples.length
      : null;

    const data = {
      hrv_ms:          avgHRV ? Math.round(avgHRV * 10) / 10 : null,
      resting_hr_bpm:  avgRHR ? Math.round(avgRHR)           : null,
      hrv_score:       hrvScore(avgHRV),
      rhr_score:       restingHRScore(avgRHR),
      sleep_score:     sleepScore(sleepSamples),
      hrv_samples:     hrvSamples.length,
      rhr_samples:     rhrSamples.length,
      sleep_samples:   sleepSamples.length,
      recorded_at:     now.toISOString(),
      source:          Capacitor.getPlatform() === 'ios' ? 'healthkit' : 'health_connect',
    };

    // Composite biometric score — weighted: HRV 50%, RHR 30%, Sleep 20%
    const weights = [
      { score: data.hrv_score,   weight: 0.50 },
      { score: data.rhr_score,   weight: 0.30 },
      { score: data.sleep_score, weight: 0.20 },
    ].filter(w => w.score !== null);

    if (weights.length) {
      const totalWeight = weights.reduce((a, w) => a + w.weight, 0);
      data.biometric_score = Math.round(
        weights.reduce((a, w) => a + w.score * w.weight, 0) / totalWeight
      );
    } else {
      data.biometric_score = null;
    }

    // Persist cache
    localStorage.setItem(CACHE_KEY, JSON.stringify({ userId, ts: Date.now(), data }));

    // Sync to backend (fire and forget — never blocks the UI)
    if (userId && data.biometric_score !== null) {
      axios.post(`${API}/biometrics/${userId}`, data).catch(() => {});
    }

    return data;

  } catch (e) {
    console.warn('[useHealthData] Health data unavailable:', e.message || e);
    return null;
  }
}

// ── React hook ────────────────────────────────────────────────────────────────

export default function useHealthData(userId) {
  const [healthData, setHealthData] = useState(() => {
    // Hydrate from cache on mount (synchronous — no flicker)
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
      if (cached.userId === userId && Date.now() - cached.ts < CACHE_TTL_MS) {
        return cached.data;
      }
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    // On web: skip silently
    if (!Capacitor.isNativePlatform()) return;

    setLoading(true);
    readHealthData(userId)
      .then(d => { if (d) setHealthData(d); })
      .finally(() => setLoading(false));
  }, [userId]);

  return { healthData, loading };
}
